<template>
  <div>

    <div v-if="!show404 && !show403">
      <!-- One page for the process and its activities (US 9369 + 9371): the hero, then the process first and its activities -->
      <ProcessHero v-if="process.id" :process="process" :row="switcherRow"></ProcessHero>
      <ProcessSwitcher ref="switcher" :process="$route.params.slug" @loaded="switcherLoaded" @changed="updateProcess"></ProcessSwitcher>

      <!-- Basic info block: the process's own header, with its Edit and delete -->
      <BasicInfoBlock class="mt-3" :object="process" @updated="updateProcess" :edit="edit"></BasicInfoBlock>

      <b-tabs class="mt-3" v-model="tabIndex">

        <!-- Basic info tab -->
        <b-tab id="process-tab-basic-info" :title="$t('processes.tab.basic_info')" @click="basicTabClicked">
          <br />
          <div class="card" qid="basic-info-section">
            <!-- Update description -->
            <DescriptionBlock :object="process" @updated="updateProcess"></DescriptionBlock>
          </div>
          <br />
          <!-- Policies list -->
          <PoliciesAndProceduresTable :object="process" @updated="updateProcess" v-if="getClient.policyModule"></PoliciesAndProceduresTable>

        </b-tab>

        <!-- GDPR tab -->
        <b-tab id="process-tab-gdpr" :title="$t('processes.tab.gdpr')" class="mt-3" @click="gdprTabClicked" v-if="getClient.gdprModule && process.privacy_feature">
          <div v-if="showGdprTab">
            <!-- Update purpose -->
            <div class="card" qid="basic-info-section">
              <PurposeBlock :object="process" @updated="updateProcess"></PurposeBlock>
            </div>
            <br />
            <!-- Legal basis table -->
            <LegalBasisTable :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></LegalBasisTable>
            <br />
            <!-- Information obligation list -->
            <InformationObligationTable :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></InformationObligationTable>
            <br />
            <Riskprofile :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></Riskprofile>
            <br />
            <Confidentiality :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></Confidentiality>
            <br />
            <Integrity :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></Integrity>
            <br />
            <Availability :object="process" @updated="updateProcess" v-if="getClient.gdprAdvancedModule"></Availability>
          </div>
        </b-tab>

        <!-- Risk scenarios tab -->
        <b-tab id="process-tab-risk-management" :title="$t('activities.tab.risk_management')" class="mt-3" @click="riskTabClicked" v-if="getClient.riskModule">
          <div v-if="showRiskTab">
            <AssetRiskTab asset="processes" catalogue-type="process" :object="process"
                :can-create="isGranted([permissions.PROCESS_RISK_SCENARIO_CREATE, permissions.PROCESS_RISK_SCENARIO_CREATE_OWN], process) && (!getClient.parent || process.partition)"
                :can-edit="isGranted([permissions.PROCESS_RISK_SCENARIO_EDIT, permissions.PROCESS_RISK_SCENARIO_EDIT_OWN], process) && (!getClient.parent || process.partition)"
                :can-delete="isGranted([permissions.PROCESS_RISK_SCENARIO_DELETE, permissions.PROCESS_RISK_SCENARIO_DELETE_OWN], process) && (!getClient.parent || process.partition)"
                :sources="switcherRow ? riskSources : null"
                @updated="updateProcess"></AssetRiskTab>
          </div>
        </b-tab>

        <!-- Process assesments tab -->
        <b-tab id="process-tab-assessments" :title="$t('systems.tab.assessments')" class="mt-3" @click="assessmentsTabClicked" v-if="getClient.assessmentModule && process.assessment_feature">
          <!-- The process's and its activities' assessments in one tab (US 9369 + 9371) -->
          <ProcessAssessmentsTab v-if="switcherRow && process.id" :process="process"
              :can-start="!!getClient.projectModule && isGranted(permissions.PROJECT_CREATE)"
              @start="$refs.createProject.showGoal('risk')"></ProcessAssessmentsTab>
          <AssessmentsTable v-else :process="process" @updated="updateProcess"></AssessmentsTable>
          <!-- Opens the project launcher on a risk assessment (US 9369 + 9371) -->
          <span class="pm-hidden-trigger">
            <CreateProjectModal v-if="getClient.projectModule" ref="createProject" type="risk_assessment" :types="['risk_assessment']"></CreateProjectModal>
          </span>
        </b-tab>

        <!-- Process activities tab -->
        <b-tab id="process-tab-activities" :title="$t('processes.tab.activities')"
               class="mt-3"
               @click="activitiesTabClicked"
               v-if="isGranted([permissions.PROCESS_ACTIVITY_VIEW, permissions.PROCESS_ACTIVITY_VIEW_OWN], process)"
        >
          <div v-if="showActivitiesTab">
            <ActivitiesTable :object="process" @updated="updateProcess"></ActivitiesTable>
          </div>
        </b-tab>

        <!-- Process history tab -->
        <b-tab id="process-tab-history" :title="$t('processes.tab.history')" class="mt-3" @click="historyTabClicked">
          <div v-if="showHistoryTab">
            <ProcessHistory :object="process"></ProcessHistory>
          </div>
        </b-tab>

      </b-tabs>
    </div>


    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>


  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import ProcessEditForm from '@/views/Processes/ProcessEditForm.vue';
import Avatar from '@/components/Avatar.vue';
import ValidationList from '@/views/Validations/ValidationList.vue';
import ValidationForm from '@/views/Validations/ValidationForm.vue';
import PoliciesAndProceduresTable from '@/views/Processes/PoliciesAndProceduresTable.vue';
import LegalBasisTable from '@/views/Processes/LegalBasisTable.vue';
import InformationObligationTable from '@/views/Processes/InformationObligationTable.vue';
import BasicInfoBlock from '@/views/Processes/BasicInfoBlock.vue';
import ProcessSwitcher from '@/components/processes/ProcessSwitcher.vue';
import ProcessHero from '@/components/processes/ProcessHero.vue';
import {processRiskSources} from '@/components/processes/riskSources.js';
import ProcessAssessmentsTab from '@/components/processes/ProcessAssessmentsTab.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import DescriptionBlock from '@/views/Processes/DescriptionBlock.vue';
import PurposeBlock from '@/views/Processes/PurposeBlock.vue';
import Confidentiality from '@/views/Processes/Confidentiality.vue';
import Riskprofile from '@/views/Processes/Riskprofile.vue';
import Integrity from '@/views/Processes/Integrity.vue';
import Availability from '@/views/Processes/Availability.vue';
import ActivitiesTable from '@/views/Processes/ActivitiesTable.vue';
import ProcessHistory from '@/views/Processes/ProcessHistory.vue';
import AssetRiskTab from '@/components/risk/AssetRiskTab.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import AssessmentsTable from '@/views/Processes/AssessmentsTable.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';


export default {
  name: 'ProcessView',
  data: function() {
    return {
      switcherRow: null,
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('processes.title'),
          to: '/processing-activities'
        }
      ],
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      showFullPurpose: false,
      process: {},
      edit: this.$route.params.edit,
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,
      lbValidationShow: false,

      itemToDelete: {},
      processGroups: [],
      tags: [],
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      selectedArticle: [],
      articles: [],
      selectedFiles: [],
      showArticleDescription: false,
      selectedLB: {},
      viewLBModal: false,
      editLBModal: false,
      updateValidationList: false,
      selectedLBSort: '',
      selectedLBDirection: '',
      showDescriptionInput: false,
      showPurposeInput: false,
      processEditDescription: '',
      processEditPurpose: '',
      showActivitiesTab: false,
      showAssessmentsTab: false,
      showHistoryTab: false,
      showRiskTab: false,
      showGdprTab: false,
      buttonDisabled: false,
      myTasks: [],
      myGuides: [],
      tabIndex: 0,
      selectedItem: {},
      showTagsModal: false,
      show404: false,
      show403: false,
    }
  },
  computed: {
    // The process's own risk scenarios and its activities' in one tab (US 9369 + 9371)
    riskSources() {
      return processRiskSources(this, this.process, this.switcherRow, null);
    },
    getClient() {
      return this.$store.state.client
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadPage() {
      return this.$store.state.updatePage
    },
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        let self = this;
        setTimeout(function(){
          if (self.$route.name == 'processes.view') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent(true);
      }
    },
    getReloadContent: function () {
      if (!this.showOnTask) {
        if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
          if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
            this.process = this.$store.state.updatedObject
          }
        }
      }
    },
    getReloadPage: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id && this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
        this.$router.push({ name: 'processes.view', params: { slug: this.$store.state.updatedObject.slug }, query: { task: this.$route.query.task }})
      } else {
        this.$router.push({ name: 'processes.view', params: { slug: this.process.slug }, query: { task: this.$route.query.task }})
      }
    },
    getTabIndex: function (newValue, oldValue) {

      this.handleUpdateTab(newValue)
    }
  },
  components: {
    ProcessSwitcher,
    ProcessHero,
    ProcessAssessmentsTab,
    CreateProjectModal,
    ProcessEditForm,
    Avatar,
    ValidationList,
    ValidationForm,
    Integrity,
    Availability,
    Confidentiality,
    Riskprofile,
    ActivitiesTable,
    ProcessHistory,
    AssetRiskTab,
    ShowMoreModal,
    AssessmentsTable,
    DescriptionBlock,
    PurposeBlock,
    PoliciesAndProceduresTable,
    LegalBasisTable,
    InformationObligationTable,
    BasicInfoBlock,
    page403,
    page404,
  },
  methods: {
    availableTabs(process) {
      let self = this;

      return [
        { code: 'basic-info', visible: true },
        { code: 'gdpr', visible: this.getClient.gdprModule && process.privacy_feature },
        { code: 'risk-management', visible: this.getClient.riskModule },
        { code: 'assessments', visible: this.getClient.assessmentModule && process.assessment_feature},
        { code: 'activities', visible: true },
        { code: 'history', visible: true },
      ].filter(tab => tab.visible);
    },
    setTabFromQuery(tabCode, process) {

      const visibleTabs = this.availableTabs(process);

      // Find the index of the tab with the matching code
      const tab = visibleTabs.find(tab => tab.code === tabCode);
      const tabIndex = visibleTabs.findIndex(tab => tab.code === tabCode);

      if (tab && tab.code == 'gdpr') {
        this.tabIndex = tabIndex
        this.gdprTabClicked()
      }
      if (tab && tab.code == 'assessments') {
        this.tabIndex = tabIndex
        this.assessmentsTabClicked()
      }
      if (tab && tab.code == 'risk_management') {
        this.tabIndex = tabIndex
        this.riskTabClicked()
      }
      if (tab && tab.code == 'activities') {
        this.tabIndex = tabIndex
        this.activitiesTabClicked()
      }
      if (tab && tab.code == 'history') {
        this.tabIndex = tabIndex
        this.historyTabClicked()
      }
      if (tab && tab.code == 'basic-info') {
        this.tabIndex = tabIndex
        this.basicTabClicked()
      }

      console.log("Tab Index Set To:", this.tabIndex);

    },
    goToList() {
      this.$router.push('/processing-activities')
    },
    openTagsModal(object) {
      this.selectedItem = object
      this.showTagsModal = true
    },
    closeTagsModal() {
      this.selectedItem = {}
      this.showTagsModal = false
    },
    getReducedTags(tags) {
      let reducedTags = []
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    openToolbar(object) {
      this.$store.commit('storeTaskToolbarState', true);
      this.$store.commit('storeActiveTask', object);
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Process' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Process',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    openHelpCenter(object) {
      let topElement = document.getElementById("app");
      if (topElement.classList.contains("guide-collapsed")) {
        topElement.classList.toggle("guide-collapsed");
      }
      if (!topElement.classList.contains("activity-collapsed")) {
        topElement.classList.toggle("activity-collapsed");
      }

      this.$store.commit('storeHelpCenterState', true);
      this.$store.commit('storeActiveGuideScope', object);
    },
    handleUpdateTab(response) {
      if (response == 'eu-gdpr') {
        this.tabIndex = 1
        this.gdprTabClicked()
      }
      if (response == 'risk-management') {
        let index = 2;

        if (!this.getClient.gdprAdvancedModule) {
          index = index - 1;
        }

        this.tabIndex = index
        this.riskTabClicked()
      }
      if (response == 'basic-info') {
        this.tabIndex = 0
        this.basicTabClicked()
      }
    },
    basicTabClicked() {
      this.showHistoryTab = false;
      this.showAssessmentsTab = false;
      this.showRiskTab = false;
    },
    updateProcess() {
      this.loadContent();
      // Activities added or removed, or the legal basis changed: the row and the hero follow
      if (this.$refs.switcher) {
        this.$refs.switcher.reload();
      }
    },
    gdprTabClicked() {
      this.showHistoryTab = false;
      this.showGdprTab = true;
      this.showRiskTab = false;
      this.showAssessmentsTab = false;
    },
    activitiesTabClicked() {
      this.showActivitiesTab = true;
      this.showAssessmentsTab = false;
      this.showHistoryTab = false;
      this.showRiskTab = false;
    },
    assessmentsTabClicked() {
      this.showAssessmentsTab = true;
      this.showHistoryTab = false;
      this.showRiskTab = false;
    },
    riskTabClicked() {
      this.showRiskTab = true;
      this.showHistoryTab = false;
      this.showAssessmentsTab = false;
    },
    historyTabClicked() {
      this.showHistoryTab = true;
      this.showRiskTab = false;
      this.showAssessmentsTab = false;
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    reloadContent(object) {
      // If slug is not changed, just reload content
      if (object.slug == this.process.slug) {
        // Remove last item in breadcrumbs, because it will be added again on load content
        this.breadcrumbItems.pop();
        this.loadContent(true);
      } else {
        this.$store.commit('storeUpdateTasks', true);
        // If slug changed, reload state with new param
        this.$router.push({ name: 'processes.view', params: { slug: object.slug }})
      }
    },
    updateValidation(validation) {
      // Event emmited from validation component, update active validation
      this.process.validation = validation
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-process'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-process'].hide()
    },
    confirmDeleteTP(object) {
      let self = this;
      piincHttp.delete('processes/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();

        let objectP = {
          type: 'processes',
          filters: {}
        }
        self.$store.commit('storeActiveFilters', objectP);
        self.$store.commit('storeUpdateTasks', true);
        self.$store.commit('storeActiveTask', {});
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('processes.delete.success'))
      }, function() {});
    },
    // A process with one activity opens on the activity, unless the process itself was picked (view=process) or a tab asked for
    switcherLoaded(row) {
      this.switcherRow = row;
      if (!row || this.$route.query.view || this.$route.query.tab || this.$route.params.tabIndex) {
        return;
      }
      let own = row.children.filter(child => !child.shared);
      if (own.length === 1) {
        this.$router.replace({ name: 'processes.activity', params: { process: row.parent.slug, slug: own[0].slug }, query: this.$route.query });
      }
    },
    loadContent(breadcrumbPush) {
      let self = this;
      piincHttp.get('processes/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.process = response.data;
        self.loadTasks('update_basic_info');
        if (breadcrumbPush) {
          // Push Third party name in breadcrumb array
          self.breadcrumbItems.push({text: self.process.name, active: true});
        }
        setTimeout(function(){
          self.setTabFromQuery(self.$route.query.tab, self.process);
        }, 1000);

      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.process.who_am_i,
            model_id: this.process.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('storeTabIndex', '');
    next()
  },
  created () {
    let self = this;
    if (this.getClient && this.getClient.slug) {

      this.loadContent(true);

    }


    if (this.$route.params.tabIndex && this.$route.params.validation) {
      setTimeout(function(){
        self.tabIndex = self.$route.params.tabIndex
        if (self.tabIndex == 1) {
          self.gdprTabClicked()
        }
        if (self.tabIndex == 2) {
          self.riskTabClicked()
        }

      }, 1000);
      setTimeout(function(){
        var element = document.getElementsByClassName(self.$route.params.validation);
        element[0].scrollIntoView(true);
        window.scrollBy(0, -100);
        element[0].classList.add("highlight-validation");

      }, 3000);
    }


    this.loadGuides('update_basic_info');
  }
}
</script>
